import { CustomTableProps, Column, MultipleRowActionType } from './CustomTable'
import { action, makeAutoObservable } from 'mobx'
import Tooltip from '@mui/material/Tooltip'
import IconButton from '@mui/material/IconButton'
import React from 'react'
import RefreshIcon from '@mui/icons-material/Refresh'
import CircularProgress from '@mui/material/CircularProgress'
import { CustomTableMultipleRowAction } from './toolbar/CustomTableMultipleRowAction'
import { CustomTableLocalRow } from './CustomTableLocalRow'
import { IStoreWithReactions } from 'reusable/mobx-store-utils/useStore'
import DisposableReactionsStore from 'reusable/mobx-store-utils/DisposableReactionsStore'
import { deferAction } from 'reusable/utils/utils'
import { notNull } from '@gap/utils/utils'
import { createStoreContext } from 'reusable/mobx-store-utils/storeUtils'
import ObservableRef from 'reusable/mobx-store-utils/ObservableRef'

interface CustomTableStoreProps<RowData extends AnyObject, RowStore>
   extends CustomTableProps<RowData, RowStore> {
   windowSize: {
      height: number
      width: number
   }
}
export class CustomTableStore<RowData extends AnyObject, RowStore> implements IStoreWithReactions {
   nonObservables = {
      lastIsRemoteData: undefined as boolean | undefined,
   }

   sp: CustomTableStoreProps<RowData, RowStore>
   paginationDivRef = new ObservableRef<HTMLDivElement>()
   toolbarDivRef = new ObservableRef<HTMLDivElement>()
   selectedRowByClick?: RowData
   fullscreen = false
   page = 0
   searchString = ''
   columnSortingIndex = -1
   columnSortingDirection = false as 'asc' | 'desc' | false
   rowsPerPage: number
   headerCellTopValue = 0
   loading = false
   filteredRows = [] as CustomTableLocalRow<RowData, RowStore>[]
   filteredAndSortedRows = [] as CustomTableLocalRow<RowData, RowStore>[]
   filteredSortedAndPagedRows = [] as CustomTableLocalRow<RowData, RowStore>[]
   divMaxHeight = undefined as number | undefined
   paginationBoxMinHeight = '64px'
   localRows = [] as CustomTableLocalRow<RowData, RowStore>[]
   maxCount = 0
   waitingForRowsToCome = false
   localSize?: 'small' | 'medium'
   tableRowNextIdSequence = 0
   timeout?: NodeJS.Timeout

   constructor(sp: CustomTableStoreProps<RowData, RowStore>) {
      makeAutoObservable(this, {
         nonObservables: false,
      })
      this.sp = sp
      this.rowsPerPage =
         (!!sp.pageSizeOptions && sp.pageSizeOptions.length > 0 && sp.pageSizeOptions[0]) || 5
      this.localSize = sp.size
   }

   get fullscreenable() {
      return this.sp.fullscreenable
   }

   get rows() {
      return this.isRemoteData ? this.localRows : this.filteredSortedAndPagedRows
   }

   get searchable() {
      return this.sp.searchable === undefined || !!this.sp.searchable
   }

   get pagHeight() {
      return this.sp.pagination && this.paginationDivRef?.current
         ? this.paginationDivRef?.current?.clientHeight
         : 0
   }

   get toolbarHeight() {
      return this.sp.pagination && this.toolbarDivRef?.current
         ? this.toolbarDivRef?.current?.clientHeight
         : 0
   }

   get size() {
      if (this.sp.userCanToggleSize) {
         return this.localSize
      } else {
         return this.sp.size
      }
   }

   get maxHeight() {
      return (this.fullscreen ? this.sp.windowSize.height - 32 : this.sp.maxHeight) || 0
   }

   get minHeight() {
      return (this.fullscreen ? this.sp.windowSize.height - 32 : this.sp.minHeight) || 0
   }

   get hasCollapsibleRowDetail() {
      return !!this.sp.collapsibleDetailConfig
   }

   get isRemoteData() {
      return !!this.sp.remoteDataFetchRequest
   }

   get totalCount() {
      if (this.isRemoteData) {
         return this.sp.totalCount || 0
      } else {
         return this.filteredRows.length || 0
      }
   }

   get hasRefreshButton() {
      if (this.sp.forceHasRefresh !== undefined) {
         return this.sp.forceHasRefresh
      }
      return this.isRemoteData
   }

   get isLoading() {
      if (this.sp.forceIsLoading !== undefined) {
         return this.sp.forceIsLoading
      }
      return this.loading
   }

   get scrollableContainerStyles() {
      return {
         display: 'inline-block',
         maxHeight: this.divMaxHeight,
         overflow: this.hasScrolling ? 'auto' : undefined,
      } as React.CSSProperties
   }

   get hasScrolling() {
      return this.sp.maxHeight !== undefined
   }

   get freeActions() {
      if (this.sp.actions && this.sp.actions.freeActions) {
         return this.sp.actions.freeActions
      } else {
         return []
      }
   }

   get rowActions() {
      if (this.sp.actions && this.sp.actions.rowActions) {
         return this.sp.actions.rowActions
      } else {
         return []
      }
   }

   get multipleActions() {
      if (this.sp.actions && this.sp.actions.multipleRowActions) {
         return this.sp.actions.multipleRowActions
      } else {
         return []
      }
   }

   get buttons() {
      const buttons = []
      let index = 0
      if (this.sp.additionalToolbarControls) {
         for (const element of this.sp.additionalToolbarControls) {
            buttons.push(element)
         }
      }
      if (this.rowsSelectedCount > 0) {
         for (const action of this.multipleActions) {
            let realAction: MultipleRowActionType<RowData> | undefined
            const rows = this.localRows.filter(l => l.selected).map(l => l.row)
            if (typeof action === 'function') {
               realAction = action(rows)
            } else {
               realAction = action
            }
            if (realAction) {
               if (realAction.icon) {
                  buttons.push(
                     <CustomTableMultipleRowAction
                        key={index++}
                        title={realAction.title || ''}
                        callback={realAction.callback}
                        icon={realAction.icon}
                     />
                  )
               } else {
                  throw new Error('Free actions must have an icon')
               }
            }
         }
      }
      for (const action of this.freeActions) {
         if (action.icon) {
            buttons.push(
               <Tooltip title={action.title ?? ''} key={index++}>
                  <IconButton disabled={action.disabled} onClick={action.callback} size="large">
                     {action.icon()}
                  </IconButton>
               </Tooltip>
            )
         } else {
            throw new Error('Free actions must have an icon')
         }
      }
      if (this.hasRefreshButton) {
         buttons.push(
            <React.Fragment key={index++}>
               {this.isLoading ? (
                  <Tooltip title="Loading...">
                     <IconButton size="large">
                        <CircularProgress disableShrink size={24} />
                     </IconButton>
                  </Tooltip>
               ) : (
                  <Tooltip title="Refresh Data" key={index++}>
                     <IconButton onClick={this.dataRefreshCheckLoading} size="large">
                        <RefreshIcon />
                     </IconButton>
                  </Tooltip>
               )}
            </React.Fragment>
         )
      }
      return buttons
   }

   get headerCellStyle() {
      const style =
         (this.sp.styling &&
            this.sp.styling.headerStyle && {
            ...this.sp.styling.headerStyle,
         }) ||
         {}
      style.position = 'sticky'
      style.top = this.headerCellTopValue
      style.zIndex = 1
      return style
   }

   get actionColumnHeaderStyle() {
      const style = { ...this.headerCellStyle }
      style.width = '1px'
      style.paddingLeft = 32
      style.paddingRight = 32
      return style
   }

   get pageSizeOptionsRender() {
      const possiblePageSizes = [...(this.sp.pageSizeOptions || [])]

      const options = [] as number[]
      for (let index = 0; index < possiblePageSizes.length; index++) {
         const possiblePageSize = possiblePageSizes[index]

         if (possiblePageSize === this.maxCount) {
            options.push(possiblePageSize)
            break
         } else if (possiblePageSize > this.maxCount) {
            options.push(possiblePageSize)
            break
         } else {
            options.push(possiblePageSize)
         }
      }
      return options
   }

   get rowsSelectedCount() {
      return this.localRows.filter(r => r.selected).length
   }

   get rowsSelectedCountPage() {
      return this.filteredSortedAndPagedRows.filter(r => r.selected).length
   }

   get tableStyle() {
      const style = ((this.sp.styling || {}).style || {}) as React.CSSProperties
      style.borderCollapse = 'separate'
      return style
   }

   set isLoading(value: boolean) {
      if (this.loading !== value) {
         this.loading = value
      }
   }

   configureReactions = (disposable: DisposableReactionsStore) => {
      disposable.registerAutorun({
         observer: () => {
            if (this.hasScrolling) {
               const divMaxHeight = this.maxHeight - this.pagHeight - this.toolbarHeight
               this.setDivMaxHeight(divMaxHeight)
            }
            this.setPaginationBoxMinHeight(this.pagHeight + 'px')
         },
      })

      disposable.registerAutorun({
         observer: () => {
            this.sp.setReloadFunction &&
               this.sp.setReloadFunction(() => this.dataFetch('forced-reload'))
         },
      })

      disposable.registerAutorun({
         observer: () => {
            this.sp.setStoreReference && this.sp.setStoreReference(this)
         },
      })

      disposable.registerAutorun({
         observer: () => {
            if (this.sp.pageSize) {
               !!this.sp.pageSize && this.setRowsPerPage(this.sp.pageSize)
            }
         },
      })

      disposable.registerAutorun({
         observer: () => {
            for (let index = 0; index < this.sp.columns.length; index++) {
               const column = this.sp.columns[index]
               if (column.defaultSort) {
                  this.setColumnSort(index, column.defaultSort)
                  break
               }
            }
         },
      })

      disposable.registerAutorun({
         observer: () => {
            let updateRows = false

            if (this.nonObservables.lastIsRemoteData !== this.isRemoteData) {
               this.nonObservables.lastIsRemoteData = this.isRemoteData
               this.log('Rows are going to be updated. Reason: isRemoteData changed')
               updateRows = true
            }

            // CustomTable holds reference of the individual objects passed as row items,
            // so the comparison is only on the item reference level
            if (this.sp.rows.length !== this.getLocalRowLength()) {
               this.log('Rows are going to be updated. Reason: rows length changed')
               updateRows = true
            } else {
               if (this.sp.rows.some((row, index) => this.getLocalRow(index).row !== row)) {
                  this.log('Rows are going to be updated. Reason: one of the row items changed')
                  updateRows = true
               }
            }

            if (updateRows) {
               if (!this.isRemoteData) {
                  this.setLoading()
               }
               this.setLocalRows(this.sp.rows)
               if (!this.isRemoteData) {
                  this.dataFetch('data-change')
               }
            }
         },
      })

      disposable.registerAutorun({
         observer: () => {
            this.setSearchString(this.sp.initialSearch)
         },
      })

      disposable.registerAutorun({
         observer: () => {
            let count = 0
            if (this.isRemoteData) {
               count = this.sp.totalCount || 0
            } else {
               count = this.sp.rows.length
            }
            if (count > this.maxCount) {
               this.setMaxCount(count)
            }
         },
      })

      disposable.registerAutorun({
         observer: () => {
            if (this.sp.fullscreen !== undefined) {
               this.toggleFullscreen(this.sp.fullscreen)
            }
         },
      })
   }

   getLocalRow = (index: number) => this.localRows[index]

   getLocalRowLength = () => this.localRows.length

   setLoading = () => {
      this.loading = true
   }

   setColumnSort = (index: number, direction: 'asc' | 'desc' | false) => {
      this.columnSortingIndex = index
      this.columnSortingDirection = direction
   }

   setRowsPerPage = (rowsPerPage: number) => {
      this.rowsPerPage = rowsPerPage
   }

   setSearchString = (str?: string) => {
      this.searchString = str || ''
   }

   setMaxCount = (maxCount: number) => {
      this.maxCount = maxCount
   }

   log(msg: string) {
      if (!!this.sp.diagnosticLogs) {
         console.log(msg)
      }
   }

   toggleSize = () => {
      this.loading = true
      deferAction(() => {
         this.localSize = this.localSize === 'small' ? 'medium' : 'small'
         this.loading = false
      })
   }

   setDivMaxHeight = (divMaxHeight: number) => {
      if (divMaxHeight !== this.divMaxHeight) {
         deferAction(() => {
            this.divMaxHeight = divMaxHeight
         })
      }
   }

   setPaginationBoxMinHeight = (minHeight: string) => {
      if (minHeight !== this.paginationBoxMinHeight && minHeight !== '0px') {
         deferAction(() => {
            this.paginationBoxMinHeight = minHeight
         })
      }
   }

   toggleFullscreenByUser = () => this.toggleFullscreen()

   toggleFullscreen = (force?: boolean) => {
      const isForced = force !== undefined
      if (isForced && force === this.fullscreen) {
         return
      }
      this.loading = true
      deferAction(() => {
         this.fullscreen = isForced ? force : !this.fullscreen

         !isForced && this.sp.fullscreenExited && this.sp.fullscreenExited()

         this.loading = false
      })
   }

   getCollapsibleConfig(row: RowData) {
      if (this.sp.collapsibleDetailConfig) {
         return this.sp.collapsibleDetailConfig(row)
      }
      return
   }

   setLocalRows = (rows: RowData[]) => {
      const insertWhenDifferent = false
      // when it's remote data, or the localRows are not initialized yet, or the new rows are none, just update straight away
      if (
         this.isRemoteData ||
         this.localRows.length === 0 ||
         !this.sp.preserveSelectionWhenRowsUpdated ||
         rows.length === 0
      ) {
         this.localRows = rows.map(
            row =>
               new CustomTableLocalRow<RowData, RowStore>({
                  id: this.tableRowNextIdSequence++,
                  row,
                  collapsibleConfig: this.getCollapsibleConfig(row),
                  rowStore: this.sp.createRowStore ? this.sp.createRowStore(row) : undefined
               })
         )
         deferAction(() => (this.waitingForRowsToCome = false), 500)
         return
      }

      // max rows between the already existing ones and the new ones
      let max = this.localRows.length > rows.length ? this.localRows.length : rows.length
      for (let index = 0; index < max; index++) {
         const source = index < rows.length ? rows[index] : undefined
         const target = index < this.localRows.length ? this.localRows[index] : undefined

         const rowStore = this.sp.createRowStore && source ? { rowStore: this.sp.createRowStore(source) } : {}

         if (!!source && !target) {
            // if there isn't a target for the new row, just creates it
            this.localRows.push(
               new CustomTableLocalRow<RowData, RowStore>({
                  id: this.tableRowNextIdSequence++,
                  row: source,
                  collapsibleConfig: this.getCollapsibleConfig(source),
                  ...rowStore
               })
            )
         } else if (!source && !!target) {
            // if there is a target but no source row, delete the target
            this.localRows.splice(index, 1)
            index--
            max--
         } else if (source && target) {
            // if there is source and target
            if (source !== target.row) {
               // only modifies stuff if the reference of the object is different (like, a different item)

               if (insertWhenDifferent) {
                  this.localRows.splice(
                     index,
                     0,
                     new CustomTableLocalRow<RowData, RowStore>({
                        id: this.tableRowNextIdSequence++,
                        row: source,
                        collapsibleConfig: this.getCollapsibleConfig(source),
                        ...rowStore
                     })
                  )
                  max++
               } else {
                  this.localRows.splice(
                     index,
                     1,
                     new CustomTableLocalRow<RowData, RowStore>({
                        id: this.tableRowNextIdSequence++,
                        row: source,
                        collapsibleConfig: this.getCollapsibleConfig(source),
                        ...rowStore
                     })
                  )
               }
            }
         }
      }
      deferAction(() => (this.waitingForRowsToCome = false), 500)
   }

   handleChangePage = (page: number) => {
      this.page = page
      this.dataFetch('page-change')
   }

   handleChangeRowsPerPage = (rowsPerPage: number) => {
      this.page = 0
      this.loading = true
      deferAction(() => {
         this.rowsPerPage = rowsPerPage
         this.dataFetch('page-size-change')
      })
   }

   onHeaderCellClick = (index: number) => {
      if (index !== this.columnSortingIndex) {
         this.columnSortingDirection = false
      }
      switch (this.columnSortingDirection) {
         case false:
            this.columnSortingDirection = 'asc'
            this.columnSortingIndex = index
            break
         case 'asc':
            this.columnSortingDirection = 'desc'
            this.columnSortingIndex = index
            break
         case 'desc':
            this.columnSortingDirection = false
            this.columnSortingIndex = -1
            break
      }
      this.dataFetch('sorting-change')
   }

   dataRefresh = async () => {
      this.dataFetch('refresh-button')
   }

   dataRefreshCheckLoading = async () => {
      if (!this.isLoading) {
         this.dataRefresh()
      }
   }

   dataFetch = async (reason: DataFetchReason) => {
      if (reason === 'refresh-button') {
         if (this.sp.forceHasRefresh && this.sp.refreshButtonAction) {
            this.sp.refreshButtonAction()
            return
         }
      }

      deferAction(async () => {
         this.isLoading = true
         if (this.isRemoteData) {
            this.waitingForRowsToCome = true
         }
      })
      deferAction(async () => {
         if (reason === 'search-change' || reason === 'clear-search') {
            this.page = 0
         }
         if (this.isRemoteData) {
            await notNull(this.sp.remoteDataFetchRequest)({
               page: this.page,
               pageSize: !!this.sp.pagination ? this.rowsPerPage : undefined,
               searchString: this.searchString,
               sortDirection: this.columnSortingDirection || undefined,
               sortField:
                  this.columnSortingIndex >= 0
                     ? this.sp.columns[this.columnSortingIndex].field || undefined
                     : undefined,
            })
         } else {
            await this.filterData(reason)
         }
         this.isLoading = false
      })
   }
   isRowCompliantWithFilter = (localRow: CustomTableLocalRow<RowData, RowStore>) => {
      const anyCompliantColumn = !!this.sp.columns.find(c => {
         const rawData = this.getColumnRawData(localRow, c)
         if (c.columnCustomLocalSearch) {
            c.columnCustomLocalSearch(localRow.row, rawData, localRow.rowStore, this.searchString)
         }
         if (rawData !== undefined && rawData !== null) {
            const raw = rawData.toString().toUpperCase()
            if (raw.includes(this.searchString.trim().toUpperCase())) {
               return true
            } else {
               return false
            }
         }
         return false
      })

      if (anyCompliantColumn) {
         return true
      } else if (this.sp.customLocalSearch) {
         return this.sp.customLocalSearch(localRow.row, this.searchString, localRow.rowStore)
      }

      return false
   }

   getColumnRawData = (
      localRow: CustomTableLocalRow<RowData, RowStore>,
      column: Column<RowData, RowStore>,
      isSorting = false
   ) => {
      if (typeof column.searchable === 'boolean' && !column.searchable) {
         return undefined
      }
      let rawData = undefined as any
      const func = isSorting
         ? column.rawDataForSorting || column.rawDataForSearchingAndSorting
         : column.rawDataForSearchingAndSorting
      if (func) {
         rawData = func(localRow.row, column, localRow.rowStore)
      } else if (column.render) {
         const rendered = column.render(localRow.row, localRow.rowStore)
         if (
            typeof rendered == 'string' ||
            typeof rendered == 'number' ||
            typeof rendered == 'bigint' ||
            typeof rendered == 'boolean'
         ) {
            rawData = rendered
         }
      }
      if ((rawData === undefined || rawData === null) && column.field) {
         rawData = localRow.row[column.field]
      }
      return rawData
   }

   sortByColumn = (
      rows: CustomTableLocalRow<RowData, RowStore>[],
      column: Column<RowData, RowStore>
   ) => {
      const factor = this.columnSortingDirection === 'asc' ? 1 : -1
      return rows.slice().sort((a, b) => {
         const aData = this.getColumnRawData(a, column, true)
         const bData = this.getColumnRawData(b, column, true)
         if (aData < bData) {
            return -1 * factor
         } else if (aData > bData) {
            return 1 * factor
         } else {
            return 0
         }
      })
   }

   applyPagination = (rows: CustomTableLocalRow<RowData, RowStore>[]) => {
      return rows.slice(this.page * this.rowsPerPage, (this.page + 1) * this.rowsPerPage)
   }

   filterData = async (reasonParam: DataFetchReason) => {
      let reason = reasonParam
      switch (reason) {
         case 'clear-search':
         case 'search-change':
         case 'initial-mount':
         case 'data-change':
         case 'forced-reload':
         case 'refresh-button': {
            // filters by search string
            if (!this.searchString) {
               this.filteredRows = [...this.localRows]
            } else {
               this.filteredRows = this.localRows.filter(r => this.isRowCompliantWithFilter(r))
            }
            const options = this.pageSizeOptionsRender
            if (!options.find(o => o === this.rowsPerPage)) {
               this.rowsPerPage = options[options.length - 1]
            }
            reason = 'sorting-change'
         }
      }

      switch (reason) {
         case 'sorting-change': {
            // order
            if (this.columnSortingIndex >= 0) {
               this.filteredAndSortedRows = this.sortByColumn(
                  this.filteredRows,
                  this.sp.columns[this.columnSortingIndex]
               )
            } else {
               this.filteredAndSortedRows = this.filteredRows
            }
            reason = 'page-change'
         }
      }

      switch (reason) {
         case 'page-change':
         case 'page-size-change': {
            // pagination
            if (this.sp.pagination) {
               const div = this.totalCount / this.rowsPerPage
               if (this.page >= div) {
                  this.page = div <= 0 ? 0 : div - 1
               }
               this.filteredSortedAndPagedRows = this.applyPagination(this.filteredAndSortedRows)
            } else {
               this.filteredSortedAndPagedRows = this.filteredAndSortedRows
            }
         }
      }
   }

   clearSearchString = () => {
      this.searchString = ''
      this.dataFetch('clear-search')
   }

   onSearchChanged = async () => {
      deferAction(() => (this.isLoading = true))
      if (this.timeout) {
         clearTimeout(this.timeout)
      }
      this.timeout = setTimeout(
         action(async () => {
            this.timeout = undefined
            this.dataFetch('search-change')
         }),
         this.sp.searchDebounceInterval || 200
      )
   }

   selectPageAll = () => {
      deferAction(() => (this.loading = true))
      deferAction(() => {
         for (const localRow of this.filteredSortedAndPagedRows) {
            localRow.selected = true
         }
         this.sp.onSelectAll && this.sp.onSelectAll('select-page-all')
         this.loading = false
      })
   }

   selectAll = () => {
      this.loading = true
      deferAction(() => {
         for (const localRow of this.localRows) {
            localRow.selected = true
         }
         this.sp.onSelectAll && this.sp.onSelectAll('select-all')
         this.loading = false
      })
   }

   deselectAll = () => {
      this.loading = true
      deferAction(() => {
         for (const localRow of this.localRows) {
            localRow.selected = false
         }
         this.sp.onSelectAll && this.sp.onSelectAll('deselect-all')
         this.loading = false
      })
   }

   toggleRowSelection = async (localRow: CustomTableLocalRow<RowData, RowStore>) => {
      const toggled = !localRow.selected
      if (
         !this.sp.willToggleRowSelection ||
         (await this.sp.willToggleRowSelection(localRow.row, toggled))
      ) {
         localRow.selected = toggled
         this.sp.onRowSelectionToggle && this.sp.onRowSelectionToggle(localRow.row, toggled)
      }
   }
}

export const { useProvidedTypedStore: useCustomTableStore, Provider: CustomTableProvider } =
   createStoreContext(CustomTableStore)

type DataFetchReason =
   | 'page-change'
   | 'page-size-change'
   | 'sorting-change'
   | 'refresh-button'
   | 'clear-search'
   | 'search-change'
   | 'initial-mount'
   | 'data-change'
   | 'forced-reload'
